/**
 * Next.js multi-zone configuration — see https://nextjs.org/docs/pages/guides/multi-zones
 *
 * This app is the default zone: it owns every path except the ones claimed below, which are
 * proxied to another Next.js app so both are served from a single domain.
 *
 * `pages` and `pathPrefixes` are independent:
 * - `/whats-new` in `pages` only — zone owns that URL, nothing under it
 * - `/product` omitted from `pages`, `/product` in `pathPrefixes` — this app keeps the
 *   landing page, the zone gets `/product/:slug`
 * - `/glossary` in both — zone owns the listing, `/glossary/:slug`, and `/glossary/sitemap.xml`
 * - `/guides` in `pathPrefixes` only — no listing, zone gets `/guides/:slug` and `/guides/sitemap.xml`
 *
 * These are plain path constants (no env vars) so the file is safe to import from client
 * components as well as from `next.config.js`.
 */

/**
 * @typedef {object} SanityZonePathPrefix
 * @property {string} prefix Nested subtree owned by `frontweb-sanity` (`${prefix}/:path+`).
 * @property {string} existsApiPath Zone endpoint that answers whether a slug exists.
 * @property {string[]} [passthroughSlugs] Single-segment paths under the prefix to proxy without an exists check.
 */

module.exports = {
  sanity: {
    /*
     * Exact paths owned by `frontweb-sanity`. Each is rewritten as-is (no trailing slash).
     * Adding a pathPrefix does not claim the prefix itself — list it here if the zone owns that too.
     */
    pages: ['/glossary', '/whats-new'],

    /*
     * Nested subtrees owned by `frontweb-sanity`. Missing slugs still 404 on this app —
     * `existsApiPath` / `passthroughSlugs` are read by `sanity-zone-not-found`.
     */
    /** @type {SanityZonePathPrefix[]} */
    pathPrefixes: [
      {
        prefix: '/guides',
        existsApiPath: '/api/guides/exists',
        passthroughSlugs: ['sitemap.xml'],
      },
      {
        prefix: '/glossary',
        existsApiPath: '/api/glossary/exists',
        passthroughSlugs: ['sitemap.xml'],
      },
    ],

    /*
     * Where that app serves its `_next` assets. Must match `assetPrefix` in its next.config.ts.
     * The middleware matcher hardcodes this string (Next matchers must be static).
     */
    assetPrefix: '/sanity-static',

    /*
     * Env-specific favicons that `frontweb-sanity` `SeoHead` links to. Namespaced like
     * `assetPrefix` so this app's root `/favicon.ico` is untouched. The rewrite proxies
     * these to the zone, which maps them onto `public/favicons`.
     */
    faviconsPrefix: '/sanity-favicon',

    /*
     * Public prefix for this zone's API routes (draft-mode enable, etc.). `/api/*` stays on
     * this app so the Sanity frontend can reuse Craft endpoints. Must match `ZONE_API_PREFIX`.
     */
    apiPrefix: '/sanity-api',
  },
};
